import { IconsProvider, Menu, TextAreaField } from '@aws-amplify/ui-react';
import { useEffect, useState } from 'react';
import { BiChevronDown, BiChevronRight } from 'react-icons/bi';
import QueryBuilder, { ActionElement, Controls, FieldSelectorProps, OptionList, RuleGroupType, RuleType, ValidationResult, ValueEditor, ValueEditorProps } from 'react-querybuilder';
import Select from 'react-select';
import { allPages } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import General from '../utils/GeneralUtils';
import { DECISION_LEVELS, FUNCTIONAL_GROUPS, MARKET_THEMES } from '../utils/BuyingGroupFilters';
import { ENUM_FIELD_VALUES } from '../utils/enum-field-values';
import './QueryEditor.scss';
import { MemoShapeOnMap } from './ShapeOnMap';
import RadiusSearchValueEditor from './RadiusSearchValueEditor';

export const operators: any = {
  'equals': 'equals',
  'not_equals': 'does not equal',
  'lt': 'less than',
  'gt': 'greater than',
  'contains': 'contains',
  'contains_word': 'contains word',
  'starts_with': 'begins with',
  'ends_with': 'ends with',
  'does_not_contain': 'does not contain',
  'exists': 'exists',
  'not_exists': 'does not exist',
  'in': 'one of',
  'not_in': 'not one of',
  'between': 'between',
}

const type2Values = [
  {
    "name": "MONTESSORI",
    "label": "Montessori"
  },
  {
    "name": "SPECIAL_EDUCATION",
    "label": "Special Education"
  },
  {
    "name": "EARLY_CHILDHOOD_DAY_CARE",
    "label": "Early Childhood Day Care"
  },
  {
    "name": "ALTERNATIVE",
    "label": "Alternative"
  },
  {
    "name": "SPECIAL_PROGRAM_EMPHASIS",
    "label": "Special Program Emphasis"
  },
  {
    "name": "VOCATIONAL_TECHNICAL",
    "label": "Vocational Technical"
  },
  {
    "name": "WALDORF",
    "label": "Waldorf"
  },
  {
    "name": "ONLINE",
    "label": "Online"
  },
  {
    "name": "MILITARY",
    "label": "Military"
  }
]

const religiousAffiliationValues = [
  {
    "name": "AFRICAN_METHODIST_EPISCOPAL",
    "label": "African Methodist Episcopal"
  },
  {
    "name": "AMISH",
    "label": "Amish"
  },
  {
    "name": "ANGLICAN",
    "label": "Anglican"
  },
  {
    "name": "ASSEMBLY_OF_GOD",
    "label": "Assembly of God"
  },
  {
    "name": "BAPTIST",
    "label": "Baptist"
  },
  {
    "name": "BRETHREN",
    "label": "Brethren"
  },
  {
    "name": "CALVINIST",
    "label": "Calvinist"
  },
  {
    "name": "CATHOLIC",
    "label": "Catholic"
  },
  {
    "name": "CHRISTIAN",
    "label": "Christian"
  },
  {
    "name": "CHURCH_OF_CHRIST",
    "label": "Church of Christ"
  },
  {
    "name": "CHURCH_OF_GOD",
    "label": "Church of God"
  },
  {
    "name": "CHURCH_OF_GOD_IN_CHRIST",
    "label": "Church of God in Christ"
  },
  {
    "name": "CHURCH_OF_THE_NAZARENE",
    "label": "Church of the Nazarene"
  },
  {
    "name": "DISCIPLES_OF_CHRIST",
    "label": "Disciples of Christ"
  },
  {
    "name": "EPISCOPAL",
    "label": "Episcopal"
  },
  {
    "name": "EVANGELICAL_LUTHERAN_CHURCH_IN_AMERICA",
    "label": "Evangelical Lutheran Church in America"
  },
  {
    "name": "FRIENDS",
    "label": "Friends"
  },
  {
    "name": "GREEK_ORTHODOX",
    "label": "Greek Orthodox"
  },
  {
    "name": "ISLAMIC",
    "label": "Islamic"
  },
  {
    "name": "JEWISH",
    "label": "Jewish"
  },
  {
    "name": "LATTER_DAY_SAINTS",
    "label": "Latter Day Saints"
  },
  {
    "name": "LUTHERAN_CHURCH_MISSOURI_SYNOD",
    "label": "Lutheran Church Missouri Synod"
  },
  {
    "name": "MENNONITE",
    "label": "Mennonite"
  },
  {
    "name": "METHODIST",
    "label": "Methodist"
  },
  {
    "name": "NONSECTARIAN",
    "label": "Nonsectarian"
  },
  {
    "name": "OTHER",
    "label": "Other"
  },
  {
    "name": "OTHER_LUTHERAN",
    "label": "Other Lutheran"
  },
  {
    "name": "PENTECOSTAL",
    "label": "Pentecostal"
  },
  {
    "name": "PRESBYTERIAN",
    "label": "Presbyterian"
  },
  {
    "name": "SEVENTH_DAY_ADVENTIST",
    "label": "Seventh Day Adventist"
  },
  {
    "name": "WISCONSIN_EVANGELICAL_LUTHERAN_SYNOD",
    "label": "Wisconsin Evangelical Lutheran Synod"
  }
]

const titleModifierValues = [
  { name: 'assistant', label: 'Assistant' },
  { name: 'associate', label: 'Associate' },
  { name: 'administrative', label: 'Administrative' },
  { name: 'chief', label: 'Chief' },
  { name: 'executive', label: 'Executive' },
  { name: 'deputy', label: 'Deputy' },
  { name: 'interim', label: 'Interim' },
  { name: 'former', label: 'Former' },
  { name: 'vice', label: 'Vice' },
  { name: 'acting', label: 'Acting' },
  { name: 'temporary', label: 'Temporary' },
  { name: 'senior', label: 'Senior' },
  { name: 'junior', label: 'Junior' },
  { name: 'substitute', label: 'Substitute' },
  { name: 'lead', label: 'Lead' },
  { name: 'head', label: 'Head' },
]

/**
 * Labels for values whose title-cased form is wrong (acronyms, numerals).
 * Every other value is shown via General.capitalizeAllWords.
 */
const titleValueLabels: Record<string, string> = {
  'stem': 'STEM',
  'cte': 'CTE',
  'mtss': 'MTSS',
  'sel': 'SEL',
  'avid': 'AVID',
  'ffa': 'FFA',
  'edtech': 'EdTech',
  'title 1': 'Title 1',
  'title ix': 'Title IX',
  'social emotional learning': 'Social Emotional Learning (SEL)',
  'ceo': 'CEO', 'cfo': 'CFO', 'coo': 'COO', 'cto': 'CTO', 'cio': 'CIO', 'cmo': 'CMO', 'cao': 'CAO',
  'cbo': 'CBO', 'chro': 'CHRO', 'cso': 'CSO', 'cdo': 'CDO', 'clo': 'CLO', 'cco': 'CCO', 'cpo': 'CPO', 'ciso': 'CISO',
};

const titleValueLabel = (value: string): string => titleValueLabels[value] ?? General.capitalizeAllWords(value);

/**
 * Title Role / Title Area options come from the same enum mirror the CRM field-mapping screen
 * shows (ENUM_FIELD_VALUES tracks @edulead/utils TitleConfig). The previous hard-coded lists
 * covered a fraction of the values stored on contacts, so customers could not exclude areas such
 * as "accounts payable" and one option ("sel") did not match any stored value at all (EDU-60).
 */
const titleEnumOptions = (field: 'titleRole' | 'titleArea') =>
  ENUM_FIELD_VALUES[field].values.map(name => ({ name, label: titleValueLabel(name) }));

const titleRoleValues = titleEnumOptions('titleRole');

const titleAreaValues = titleEnumOptions('titleArea');

export const marketThemeValues = MARKET_THEMES.map(it => ({ name: it.value, label: it.label }));
export const decisionLevelValues = DECISION_LEVELS.map(it => ({ name: it.value, label: it.label }));
export const functionalGroupValues = FUNCTIONAL_GROUPS.map(it => ({ name: it.value, label: it.label }));

const idToNameFieldMap: any = {
  title: 'Title',
  titleModifier: 'Title Modifier',
  titleRole: 'Title Role',
  titleArea: 'Title Area',
  'role.marketThemes': 'Market Theme',
  'role.decisionLevel': 'Decision Level',
  'role.functionalGroup': 'Functional Group',
  name: 'Name',
  email: 'Email',
  emailType: 'Email Type',
  emailVerificationStatus: 'Email Verification',
  emailSource: 'Email Source',
  phone: 'Phone',
  phoneUniqueness: 'Phone Uniqueness',
  fax: 'Fax',
  shapes: 'Map',
  radiusSearch: 'Radius',
  state: 'State',
  zip: 'ZIP Code',
  territoryId: 'Territory',
  districtRevTotal: 'Annual Revenue',
  agencyRevTotal: 'Annual Revenue',
  fsclRevTotal: 'Annual Revenue',
  stfTotal: 'Nr. Of Staff',
  institutionName: 'Name',
  studentsTotal: 'Nr. of Students',
  totalSchools: 'Nr. of Schools',
  totalDistricts: 'Nr. of Institutions',
  countyId: 'County',
  type: 'Type',
  institutionType: 'Type',
  religiousAffiliation: 'Religious Affiliation',
  fulltext: 'Website Text',
  grade: 'District Grade',
  schoolGrade: 'School Grade',
  tag: 'Tags',
  access: 'Access',
  labels: 'Labels',
  ncesId: 'NCES ID',
  institutionId: 'Edulead ID',
  idParentInstitution: 'Parent Institution ID',
  titleOne: 'Title I School',
  list: 'List',
  sourceAndAge: 'Source and Age',
  lastSeen: 'Last Seen',
  subtype: 'Subtype',
  model: 'Model',
}

/**
 * Customer-facing labels for the People directory (EDU-53). Applied on top of the shared
 * `getFields()` labels only when `type === 'contacts'`, so the Institutions directory keeps
 * its own wording (e.g. "Subtype" covers charter schools there, "District Type" would not).
 */
const contactFieldLabels: Record<string, string> = {
  name: 'Contact Name',
  studentsTotal: 'Student Enrollment',
  stfTotal: 'Staff Size',
  districtRevTotal: 'District Revenue',
  access: 'Public/Private',
  model: 'School Model',
  schoolGrade: 'Grade Level',
  titleOne: 'Title 1 Status',
  institutionName: 'Institution Name',
  institutionId: 'Edulead Institution ID',
  institutionType: 'Institution Type',
  subtype: 'District Type',
  zip: 'ZIP Code',
  shapes: 'Map Search',
  radiusSearch: 'Radius Search',
  emailType: 'Inbox Type',
}

const combinators = [
  { name: 'and', label: 'And' },
  { name: 'or', label: 'Or' },
]

function getOperators(selectedOperators: string[]) {
  return selectedOperators.map(it => ({ name: it, label: operators[it] })).filter(it => !!it)
}

function getAllOperators() {
  return getOperators(Object.keys(operators))
}

function parseQuery(query?: string) {
  try {
    if (!query) {
      console.warn(`query is '${query}'`)
      return null
    }
    const parsedQuery = JSON.parse(atob(query))
    return parsedQuery
  } catch (err) {
    console.error('exception parsing query')
    return null
  }
}

function serializeQuery(query: RuleGroupType) {
  try {
    if (!query) return null
    const clone = JSON.parse(JSON.stringify(query))
    delete clone.id
    if (Array.isArray(clone.rules)) {
      for (const rule of clone.rules) {
        delete rule.id
        delete rule.valueSource
        if (Array.isArray(rule.rules)) {
          for (const subrule of rule.rules) {
            delete subrule.id
            delete subrule.valueSource
          }
        }
      }
    }
    return btoa(JSON.stringify(clone))
  } catch {
    return null
  }
}

const CustomSourceSelector = (props: FieldSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTitleAdvancedOpen, setIsTitleAdvancedOpen] = useState(false);
  const [isDistrictAdvancedOpen, setIsDistrictAdvancedOpen] = useState(false);
  const [isInstitutionAdvancedOpen, setIsInstitutionAdvancedOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => { setIsOpen(isOpen) };

  function selectField(props: FieldSelectorProps, field: string) {
    props.handleOnChange(field);
    setIsOpen(false);
  }

  const fieldSet: any = {}
  for (const item of props.options as any) fieldSet[item.name] = item.label

  /** One picker row; renders nothing when the field is not supported on this page. */
  const row = (field: string) => fieldSet[field]
    ? <div className="row" onClick={() => selectField(props, field)}>{ fieldSet[field] }</div>
    : null

  const advancedToggle = (isAdvancedOpen: boolean, setIsAdvancedOpen: (value: boolean) => void) =>
    <div className="row advanced" onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}>
      Advanced { isAdvancedOpen ? <BiChevronDown /> : <BiChevronRight /> }
    </div>

  return (
    <div style={{ position: 'relative' }}>

      <IconsProvider
        icons={{
          menu: {
            menu: <><span >{ fieldSet[props.rule.field] || idToNameFieldMap[props.rule.field as any] }</span> <BiChevronDown style={{ marginLeft: 5 }} /></>,
          },
        }}
      >
        <Menu
          className='mymenu'
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
        >
          <div className='query-selector-panel'>
            <div className="outter">
              <div className="inner">
                <div style={{ gridColumn: 1 }}>
                  <div className="header">Contact</div>
                  <div className="subheader">Search by Audience</div>
                  { row('role.decisionLevel') }
                  { row('role.functionalGroup') }
                  { row('role.marketThemes') }
                  <div className="subheader">Search by Title</div>
                  { row('title') }
                  { row('titleRole') }
                  { row('titleArea') }
                  { row('titleModifier') }
                  { advancedToggle(isTitleAdvancedOpen, setIsTitleAdvancedOpen) }
                  { isTitleAdvancedOpen && <>
                    { row('name') }
                    { row('id') }
                  </> }
                  {/* EDU-53: reachability moves to the "Refine Results" bar once it exists; kept
                      here until then so email / phone filtering is still reachable */}
                  <div className="subheader">Reachability</div>
                  { row('email') }
                  { row('emailType') }
                  { row('phone') }
                  { row('phoneUniqueness') }
                </div>
                <div style={{ gridColumn: 2 }}>
                  <div className="header">Institution</div>
                  <div className="subheader">District</div>
                  { row('studentsTotal') }
                  { row('stfTotal') }
                  { row('districtRevTotal') }
                  { advancedToggle(isDistrictAdvancedOpen, setIsDistrictAdvancedOpen) }
                  { isDistrictAdvancedOpen && row('subtype') }
                  <div className="subheader">School</div>
                  { row('access') }
                  { row('model') }
                  { row('religiousAffiliation') }
                  { row('schoolGrade') }
                  { row('titleOne') }
                  <div className="subheader">Any Institution</div>
                  { row('institutionType') }
                  { advancedToggle(isInstitutionAdvancedOpen, setIsInstitutionAdvancedOpen) }
                  { isInstitutionAdvancedOpen && <>
                    { row('institutionName') }
                    { row('ncesId') }
                    { row('institutionId') }
                  </> }
                </div>
                <div style={{ gridColumn: 3 }}>
                  <div className="header">Location</div>
                  { row('state') }
                  { row('countyId') }
                  { row('zip') }
                  { row('territoryId') }
                  { row('shapes') }
                  { row('radiusSearch') }
                </div>
              </div>
            </div>
          </div>
        </Menu>
      </IconsProvider>
    </div>
  )
}

const InstitutionFieldSelector = (props: FieldSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => { setIsOpen(isOpen) };

  function selectField(props: FieldSelectorProps, field: string) {
    props.handleOnChange(field);
    setIsOpen(false);
  }

  const fieldSet: any = {}
  for (const item of props.options as any) fieldSet[item.name] = item.label

  return (
    <div style={{ position: 'relative' }}>

      <IconsProvider
        icons={{
          menu: {
            menu: <><span >{ fieldSet[props.rule.field] || idToNameFieldMap[props.rule.field as any] }</span> <BiChevronDown style={{ marginLeft: 5 }} /></>,
          },
        }}
      >
        <Menu
          className='mymenu'
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
        >
          <div className='query-selector-panel institutions'>
            <div className="outter">
              <div className="inner">
                <div style={{ gridColumn: 1 }}>
                  <div className="header">Institution</div>
                  { fieldSet['type'] && <div className="row" onClick={() => selectField(props, 'type')}>{ fieldSet['type'] }</div> }
                  { fieldSet['subtype'] && <div className="row" onClick={() => selectField(props, 'subtype')}>{ fieldSet['subtype'] }</div> }
                  { fieldSet['access'] && <div className="row" onClick={() => selectField(props, 'access')}>{ fieldSet['access'] }</div> }
                  { fieldSet['name'] && <div className="row" onClick={() => selectField(props, 'name')}>{ fieldSet['name'] }</div> }
                  { fieldSet['fulltext'] && <div className="row" onClick={() => selectField(props, 'fulltext')}>{ fieldSet['fulltext'] }</div> }
                  { fieldSet['institutionId'] && <div className="row" onClick={() => selectField(props, 'institutionId')}>{ fieldSet['institutionId'] }</div> }
                </div>
                <div style={{ gridColumn: 2 }}>
                  <div className="header">Location</div>
                  { fieldSet['state'] && <div className="row" onClick={() => selectField(props, 'state')}>{ fieldSet['state'] }</div> }
                  { fieldSet['zip'] && <div className="row" onClick={() => selectField(props, 'zip')}>{ fieldSet['zip'] }</div> }
                  { fieldSet['countyId'] && <div className="row" onClick={() => selectField(props, 'countyId')}>{ fieldSet['countyId'] }</div> }
                  { fieldSet['territoryId'] && <div className="row" onClick={() => selectField(props, 'territoryId')}>{ fieldSet['territoryId'] }</div> }
                  { fieldSet['shapes'] && <div className="row" onClick={() => selectField(props, 'shapes')}>{ fieldSet['shapes'] }</div> }
                  { fieldSet['radiusSearch'] && <div className="row" onClick={() => selectField(props, 'radiusSearch')}>{ fieldSet['radiusSearch'] }</div> }
                  { fieldSet['phone'] && <div className="row" onClick={() => selectField(props, 'phone')}>{ fieldSet['phone'] }</div> }
                  { fieldSet['fax'] && <div className="row" onClick={() => selectField(props, 'fax')}>{ fieldSet['fax'] }</div> }
                </div>
                <div style={{ gridColumn: 3 }}>
                  <div className="header">Public</div>
                  { fieldSet['titleOne'] && <div className="row" onClick={() => selectField(props, 'titleOne')}>{ fieldSet['titleOne'] }</div> }
                  { fieldSet['ncesId'] && <div className="row" onClick={() => selectField(props, 'ncesId')}>{ fieldSet['ncesId'] }</div> }
                  { fieldSet['grade'] && <div className="row" onClick={() => selectField(props, 'grade')}>{ fieldSet['grade'] }</div> }
                  <div className="header" style={{ marginTop: 20 }}>Private</div>
                  { fieldSet['religiousAffiliation'] && <div className="row" onClick={() => selectField(props, 'religiousAffiliation')}>{ fieldSet['religiousAffiliation'] }</div> }
                  { fieldSet['model'] && <div className="row" onClick={() => selectField(props, 'model')}>{ fieldSet['model'] }</div> }
                </div>
                <div style={{ gridColumn: 4 }}>
                  <div className="header">Metrics</div>
                  { fieldSet['studentsTotal'] && <div className="row" onClick={() => selectField(props, 'studentsTotal')}>{ fieldSet['studentsTotal'] }</div> }
                  { fieldSet['stfTotal'] && <div className="row" onClick={() => selectField(props, 'stfTotal')}>{ fieldSet['stfTotal'] }</div> }
                </div>
              </div>
            </div>
          </div>
        </Menu>
      </IconsProvider>
    </div>
  )
}

// Simple module-level cache to persist territories across component re-renders
let cachedTerritories: any[] | null = null;
// Cache territory names to prevent flickering when component re-renders
let cachedTerritoryNames: Map<string, string> = new Map();

// Function to invalidate territory cache - can be called from other components
export const invalidateTerritoriesCache = () => {
  cachedTerritories = null;
  cachedTerritoryNames.clear();
};

export const LazyTerritorySelector = ({ value, onChange, authInfo }: { value: string, onChange: (value: string) => void, authInfo: any }) => {
  const [territories, setTerritories] = useState<any[]>(cachedTerritories || []);
  
  // Initialize selectedTerritoryName from cache if possible
  const getInitialTerritoryName = () => {
    if (value) {
      // First try to get from territory name cache
      if (cachedTerritoryNames.has(value)) {
        return cachedTerritoryNames.get(value)!;
      }
      // Fallback to searching in cached territories
      if (cachedTerritories) {
        const territory = cachedTerritories.find(t => t.id === value);
        if (territory) {
          // Cache the name for future use
          cachedTerritoryNames.set(value, territory.name);
          return territory.name;
        }
      }
    }
    return null;
  };
  
  const [selectedTerritoryName, setSelectedTerritoryName] = useState<string | null>(() => getInitialTerritoryName());

  // Load territories from cache or API
  useEffect(() => {
    if (!authInfo?.teamId) return;

    // Use cached territories if available
    if (cachedTerritories) {
      setTerritories(cachedTerritories);
      // Update selected territory name if we have a value but no cached name yet
      if (value && !selectedTerritoryName) {
        const territory = cachedTerritories.find(t => t.id === value);
        if (territory) {
          setSelectedTerritoryName(territory.name);
        }
      }
      return;
    }

    let isMounted = true;
    
    const loadTerritories = async () => {
      try {
        const finalTerritories = await allPages(eduleadV4.territories.list({ page_size: 50 }));
        
        if (isMounted) {
          // Cache the territories and their names
          cachedTerritories = finalTerritories;
          // Populate the territory name cache
          finalTerritories.forEach((territory: any) => {
            cachedTerritoryNames.set(territory.id, territory.name);
          });
          setTerritories(finalTerritories);
        }
      } catch (error) {
        console.error('Failed to load territories:', error);
        if (isMounted) {
          setTerritories([]);
        }
      }
    };

    loadTerritories();

    return () => {
      isMounted = false;
    };
  }, [authInfo?.teamId]);

  // No need for focus or menu open handlers since we load territories immediately

  const territoryOptions = territories.map(territory => ({
    value: territory.id,
    label: territory.name
  }));

  // Update cached territory name when territories load and we have a value
  useEffect(() => {
    if (value && territories.length > 0) {
      const territory = territories.find(t => t.id === value);
      if (territory && territory.name !== selectedTerritoryName) {
        setSelectedTerritoryName(territory.name);
        // Cache the name for future use
        cachedTerritoryNames.set(value, territory.name);
      }
    }
  }, [value, territories, selectedTerritoryName]);

  // Create the selected value object - use cached name if available, otherwise find from options
  const selectedValue = value 
    ? territoryOptions.find(option => option.value === value) || 
      (selectedTerritoryName ? { value, label: selectedTerritoryName } : null)
    : null;

  const handleChange = (option: any) => {
    if (option) {
      setSelectedTerritoryName(option.label);
      // Cache the selected name for future component re-renders
      cachedTerritoryNames.set(option.value, option.label);
    } else {
      setSelectedTerritoryName(null);
    }
    onChange(option ? option.value : '');
  };

  return (
    <div style={{ width: 522 }}>
      <Select
        value={selectedValue}
        onChange={handleChange}
        options={territoryOptions}
        isClearable={true}
        placeholder="Select territory..."
        styles={{
          ...General.getCreatableStylesCompact(),
          control: (provided: any, state: any) => ({
            ...General.getCreatableStylesCompact().control?.(provided, state),
            minHeight: '32px',
            height: '32px',
            padding: '0 0 0 0'
          }),
          valueContainer: (provided: any, state: any) => ({
            ...General.getCreatableStylesCompact().valueContainer?.(provided, state),
            height: '30px',
            padding: '0 8px'
          }),
          input: (provided: any, state: any) => ({
            ...General.getCreatableStylesCompact().input?.(provided, state),
            margin: '0',
            paddingTop: '0',
            paddingBottom: '0'
          }),
          indicatorsContainer: (provided: any, state: any) => ({
            ...General.getCreatableStylesCompact().indicatorsContainer?.(provided, state),
            height: '30px'
          }),
          placeholder: (provided: any, state: any) => ({
            ...General.getCreatableStylesCompact().placeholder?.(provided, state),
            marginLeft: '0px'
          }),
          singleValue: (provided: any) => ({
            ...provided,
            marginLeft: '0px',
          })
        }}
        noOptionsMessage={() => 'No territories found'}
      />
    </div>
  );
};

const CustomValueEditor = (props: ValueEditorProps) => {
  if (['exists', 'not_exists'].includes(props?.operator)) {
    return <></>
  }

  // For numeric fields, wrap handleOnChange with validation
  if (props.fieldData.datatype === 'number') {
    const handleNumericInput = (value: string) => {
      // Allow empty string, negative sign, numbers, and decimal point
      if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
        props.handleOnChange(value);
      }
    };

    return <ValueEditor {...props} handleOnChange={handleNumericInput} />;
  }

  if (props.fieldData.datatype === 'territory') {
    return (
      <LazyTerritorySelector
        value={props.value}
        onChange={props.handleOnChange}
        authInfo={(props as any).context?.authInfo}
      />
    );
  }
  if (props.fieldData.datatype === 'map') {
    let searchParams;
    try {
      searchParams = {
        shapes: {
          json: JSON.parse(atob(props.value))
        }
      }
    } catch {}
    return (
      <div style={{ width: 890, height: 440, position: 'relative' }}>
        <MemoShapeOnMap
          searchParams={searchParams}
          onChange={(updatedShapes) => props.handleOnChange(updatedShapes)}
        />
      </div>
    )
  }
  if (props.fieldData.datatype === 'radiusSearch') {
    return (
      <RadiusSearchValueEditor
        value={props.value}
        handleOnChange={props.handleOnChange}
      />
    )
  }
  if (props.fieldData.datatype === 'textarea') {
    return (
      <div>
        {/* <div className='desc-5' style={{ position: 'absolute', top: -14 }}>{ 1000 - props.value?.length } characters remaining</div> */}
        <TextAreaField
          label=''
          rows={1}
          width={522}
          maxLength={10000}
          value={props.value}
          resize='vertical'
          placeholder={props?.fieldData?.placeholder || ''}
          onChange={(event: any) => props.handleOnChange(event.target.value)}
        />
      </div>
    )
  }
  if (props.fieldData.datatype === 'creatable') {
    if (['not_exists', 'exists'].includes(props?.operator)) {
      return <></>
    }
    return (
      <div style={{ width: 522 }}>
        <Select
          value={
            (typeof props.value === 'string' ? props.value.split(',') : Array.isArray(props.value) ? props.value : [])
              .map((it: string) => it.trim())
              .filter((it: string) => !!it)
              .map((it: string) => ({
                value: it,
                label: typeof props.fieldData?.customOptions?.getValue === 'function' ? props.fieldData.customOptions.getValue(it) : it
              }))
          }
          onChange={(event: any) => { props.handleOnChange(event.map((it: any) => it.value).join(',')) }}
          isMulti={true}
          styles={General.getCreatableStylesCompact()}
          options={props.values?.map(it => ({ value: it.name, label: it.label }))}
          isClearable={false}
        />
      </div>
    )
  }
  // Return the ValueEditor directly without wrapper to prevent focus loss
  return <ValueEditor {...props} />;
};

export default function QueryEditor({
  initialQuery,
  supportedFields,
  teamTags,
  type = 'standard',
  authInfo,
  onChange
}: {
  initialQuery?: string,
  supportedFields: string[],
  teamTags: any[],
  type: 'standard' | 'contacts' | 'institutions',
  authInfo?: any,
  onChange: (query: string | null) => void
}) {

  const [query, setQuery] = useState(parseQuery(initialQuery));

  useEffect(() => {
    setQuery(parseQuery(initialQuery))
  }, [initialQuery])

  function onQueryChange(query: any) {
    setQuery(query)
    onChange(serializeQuery(query))
  }

  function minLengthValidator(ln = 1, rule: RuleType): ValidationResult {
    return {
      valid: rule?.value?.length >= ln,
      reasons: [`Type at least ${ln} character${ln === 1 ? '' : 's'}`]
    }
  }

  function numericValidator(rule: RuleType): ValidationResult {
    return {
      valid: !isNaN(rule?.value),
      reasons: [`Must be a number`]
    }
  }

  function getFields(): OptionList {
    const fields: OptionList = [
      {
        name: 'title',
        label: 'Title',
        defaultOperator: 'contains',
        operators: getOperators(['equals', 'contains', 'does_not_contain', 'contains_word', 'starts_with', 'ends_with', 'exists']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'titleModifier',
        label: 'Title Modifier',
        datatype: 'creatable',
        defaultValue: '',
        values: titleModifierValues,
        operators: getOperators(['equals', 'not_equals', 'exists', 'not_exists']),
        customOptions: {
          getValue: (it: string) => General.capitalizeAllWords(it)
        }
      },
      {
        name: 'titleRole',
        label: 'Title Role',
        datatype: 'creatable',
        defaultValue: '',
        values: titleRoleValues,
        operators: getOperators(['equals', 'not_equals', 'exists', 'not_exists']),
        customOptions: {
          getValue: titleValueLabel
        }
      },
      {
        name: 'titleArea',
        label: 'Title Area',
        datatype: 'creatable',
        defaultValue: '',
        values: titleAreaValues,
        operators: getOperators(['equals', 'not_equals', 'exists', 'not_exists']),
        customOptions: {
          getValue: titleValueLabel
        }
      },
      {
        name: 'role.marketThemes',
        label: 'Market Theme',
        datatype: 'creatable',
        defaultValue: '',
        values: marketThemeValues,
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: (it: string) => marketThemeValues.find(theme => theme.name === it)?.label ?? General.capitalizeAllWords(it)
        }
      },
      {
        name: 'role.decisionLevel',
        label: 'Decision Level',
        datatype: 'creatable',
        defaultValue: '',
        values: decisionLevelValues,
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: (it: string) => decisionLevelValues.find(level => level.name === it)?.label ?? General.capitalizeAllWords(it)
        }
      },
      {
        name: 'role.functionalGroup',
        label: 'Functional Group',
        datatype: 'creatable',
        defaultValue: '',
        values: functionalGroupValues,
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: (it: string) => functionalGroupValues.find(group => group.name === it)?.label ?? General.capitalizeAllWords(it)
        }
      },
      {
        name: 'name',
        label: 'Name',
        defaultOperator: 'contains',
        operators: getOperators(['equals', 'contains', 'exists']),
        validator: minLengthValidator.bind(null, 1)
      },
      // `id` is the domain on the Domains page and the contact id on the People page. The API
      // maps a contact `id` to `_id`, which only works with `in` / `not_in` (a `terms` query).
      type === 'contacts'
        ? {
            name: 'id',
            label: 'Edulead Contact ID',
            datatype: 'textarea',
            placeholder: 'Comma separated list of Edulead contact IDs (max 10000 characters)',
            defaultOperator: 'in',
            operators: getOperators(['in', 'not_in']),
            validator: minLengthValidator.bind(null, 5)
          }
        : {
            name: 'id',
            label: 'Domain',
            defaultOperator: 'contains',
            operators: getOperators(['equals', 'contains', 'exists']),
            validator: minLengthValidator.bind(null, 1)
          },
      {
        name: 'email',
        label: 'Email',
        defaultOperator: 'exists',
        operators: getOperators(['equals', 'contains', 'ends_with', 'exists', 'not_equals', 'does_not_contain']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'emailType',
        label: 'Email Type',
        valueEditorType: 'select',
        defaultOperator: 'equals',
        defaultValue: 'PERSONAL',
        values: [{ label: 'Personal', name: 'PERSONAL' }, { label: 'Role (Generic Inbox)', name: 'ROLE' }],
        operators: getOperators(['equals', 'not_equals'])
      },
      {
        name: 'emailVerificationStatus',
        label: 'Email Verification',
        valueEditorType: 'select',
        defaultOperator: 'not_equals',
        defaultValue: 'INVALID',
        values: [{ label: 'Valid', name: 'VALID' }, { label: 'Valid - Catch All', name: 'CATCH_ALL' }, { label: 'Invalid', name: 'INVALID' }],
        operators: getOperators(['equals', 'not_equals'])
      },
      {
        name: 'emailSource',
        label: 'Email Source',
        valueEditorType: 'select',
        defaultOperator: 'equals',
        defaultValue: 'OBSERVED',
        values: [{ label: 'Observed', name: 'OBSERVED' }, { label: 'Generated', name: 'GENERATED' }],
        operators: getOperators(['equals', 'not_equals'])
      },
      {
        name: 'phone',
        label: 'Phone',
        defaultOperator: 'exists',
        operators: getOperators(['equals', 'contains', 'exists', 'not_equals', 'does_not_contain']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'phoneUniqueness',
        label: 'Phone Uniqueness',
        valueEditorType: 'select',
        defaultOperator: 'equals',
        defaultValue: 'UNIQUE',
        values: [
          { label: 'Unique', name: 'UNIQUE' },
          { label: 'Shared', name: 'SHARED_CONTACT,SHARED_INSTITUTION,SHARED_CONTACT_INSTITUTION' }
        ],
        operators: getOperators(['equals', 'not_equals'])
      },
      {
        name: 'fax',
        label: 'Fax',
        defaultOperator: 'exists',
        operators: getOperators(['equals', 'contains', 'exists', 'not_equals', 'does_not_contain']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'shapes',
        label: 'Map',
        datatype: 'map',
        operators: getAllOperators(),
        className: 'map'
      },
      {
        name: 'radiusSearch',
        label: 'Radius',
        datatype: 'radiusSearch',
        defaultOperator: 'equals',
        operators: getOperators(['equals']),
        className: 'radiusSearch'
      },
      {
        name: 'state',
        label: 'State',
        datatype: 'creatable',
        defaultValue: '',
        values: General.getStatesForSelect().map(it => ({ name: it.value, label: it.label })),
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: General.getStateName
        }
      },
      {
        name: 'territoryId',
        label: 'Territory',
        datatype: 'territory',
        defaultOperator: 'equals',
        defaultValue: '',
        operators: getOperators(['equals']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'zip',
        label: 'ZIP Code',
        defaultOperator: 'equals',
        operators: getOperators(['equals', 'starts_with', 'ends_with', 'not_equals']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'districtRevTotal',
        label: 'Annual Revenue',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['gt', 'lt']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'institutionName',
        label: 'Name',
        datatype: 'textarea',
        placeholder: 'Comma separated list of institution names (max 10000 characters)',
        defaultOperator: 'contains',
        operators: getOperators(['contains', 'equals']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'stfTotal',
        label: 'Nr. of Staff',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'studentsTotal',
        label: 'Nr. of Students',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['gt', 'lt']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'totalSchools',
        label: 'Nr. of Schools',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'totalDistricts',
        label: 'Nr. of Institutions',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'countyId',
        label: 'County',
        datatype: 'creatable',
        defaultValue: '',
        values: General.getCountiesForSelect(),
        operators: getOperators(['equals', 'not_equals']),
        validator: minLengthValidator.bind(null, 1),
        customOptions: {
          getValue: General.getCountiesObject
        }
      },
      {
        name: 'type',
        label: 'Type',
        valueEditorType: 'select',
        defaultOperator: 'equals',
        defaultValue: 'DISTRICT',
        values: [{
          label: 'District', name: 'DISTRICT'
        }, {
          label: 'School', name: 'SCHOOL'
        }, {
          label: 'Service Agency', name: 'AGENCY'
        }],
        operators: getOperators(['equals'])
      },
      {
        name: 'institutionType',
        label: 'Type',
        datatype: 'creatable',
        defaultValue: '',
        values: [{ name: 'DISTRICT', label: 'District' }, { name: 'SCHOOL', label: 'School' }, { name: 'AGENCY', label: 'Service Agency' }],
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: (it: string) => {
            if (it === 'DISTRICT') return 'District';
            if (it === 'SCHOOL') return 'School';
            if (it === 'AGENCY') return 'Service Agency';
            return General.capitalizeAllWords(it);
          }
        }
      },
      {
        name: 'religiousAffiliation',
        label: 'Religious Affiliation',
        datatype: 'creatable',
        defaultValue: '',
        values: religiousAffiliationValues,
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: General.getHumanReadableReligiousAffiliation
        }
      },
      {
        name: 'schoolReligiousAffiliation',
        label: 'Religious Affiliation',
        datatype: 'creatable',
        defaultValue: '',
        values: religiousAffiliationValues,
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: General.getHumanReadableReligiousAffiliation
        }
      },
      {
        name: 'titleOne',
        label: 'Title I School',
        valueEditorType: 'select',
        defaultOperator: 'equals',
        defaultValue: 'true',
        values: [{ label: 'Yes', name: 'true' }, { label: 'No', name: 'false' }],
        operators: getOperators(['equals', 'not_equals'])
      },
      {
        name: 'schoolAccess',
        label: 'Access',
        valueEditorType: 'select',
        defaultOperator: 'equals',
        defaultValue: 'public',
        values: [{ label: 'Public', name: 'PUBLIC' }, { label: 'Private', name: 'PRIVATE' }],
        operators: getOperators(['equals', 'not_equals'])
      },
      {
        name: 'model',
        label: 'Model',
        datatype: 'creatable',
        defaultValue: '',
        values: type2Values,
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: General.getHumanReadableType2
        }
      },
      {
        name: 'schoolSubtype',
        label: 'School Subtype',
        datatype: 'creatable',
        defaultValue: '',
        values: [{ name: 'REGULAR', label: 'Regular' }, { name: 'CHARTER', label: 'Charter' }],
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: (it: string) => {
            // Display "Regular" for the REGULAR value
            if (it === 'REGULAR') return 'Regular';
            if (it === 'CHARTER') return 'Charter';
            return General.capitalizeAllWords(it);
          }
        }
      },
      {
        name: 'fulltext',
        label: 'Website Text',
        operators: getOperators(['contains']),
        validator: minLengthValidator.bind(null, 5)
      },
      {
        name: 'grade',
        label: type === 'institutions' ? 'Grade' : 'District Grade',
        datatype: 'creatable',
        defaultValue: '',
        values: General.getGradesForSelect(),
        operators: getOperators(['equals']),
        customOptions: {
          getValue: General.getGradesObject
        }
      },
      {
        name: 'schoolGrade',
        label: 'School Grade',
        datatype: 'creatable',
        defaultValue: '',
        values: General.getGradesForSelect(),
        operators: getOperators(['equals']),
        customOptions: {
          getValue: General.getGradesObject
        }
      },
      {
        name: 'tag',
        label: 'Tag',
        datatype: 'creatable',
        defaultValue: '',
        values: teamTags.map(it => ({ name: it, label: it })),
        operators: getOperators(['equals'])
      },
      {
        name: 'access',
        label: 'Access',
        valueEditorType: 'select',
        defaultOperator: 'equals',
        defaultValue: 'PUBLIC',
        values: [{ label: 'Private', name: 'PRIVATE' }, { label: 'Public', name: 'PUBLIC' }],
        operators: getOperators(['equals', 'not_equals'])
      },
      {
        name: 'labels',
        label: 'Labels',
        datatype: 'creatable',
        defaultValue: '',
        values: [{ name: 'decision maker', label: 'Decision Maker' }, { name: 'educator', label: 'Educator' }],
        operators: getOperators(['in', 'not_in']),
      },
      {
        name: 'subtype',
        label: 'Subtype',
        datatype: 'creatable',
        defaultValue: '',
        values: [{ name: 'REGULAR', label: 'Regular' }, { name: 'CHARTER', label: 'Charter' }, { name: 'SPECIALIZED', label: 'Specialized' }, { name: 'COMPONENT', label: 'Component' }, { name: 'ALTERNATIVE', label: 'Alternative' }],
        operators: getOperators(['equals', 'not_equals']),
        customOptions: {
          getValue: (it: string) => {
            // Display "Regular" for the REGULAR value
            if (it === 'REGULAR') return 'Regular';
            if (it === 'CHARTER') return 'Charter';
            if (it === 'SPECIALIZED') return 'Specialized';
            if (it === 'COMPONENT') return 'Component';
            if (it === 'ALTERNATIVE') return 'Alternative';
            return General.capitalizeAllWords(it);
          }
        }
      },
      {
        name: 'ncesId',
        label: 'NCES ID',
        datatype: 'textarea',
        placeholder: 'Comma separated list of NCES IDs (max 10000 characters)',
        operators: getOperators(['equals', 'not_equals']),
        validator: minLengthValidator.bind(null, 5)
      },
      {
        name: 'institutionId',
        label: 'Edulead ID',
        datatype: 'textarea',
        placeholder: 'Comma separated list of Edulead IDs (max 10000 characters)',
        operators: getOperators(['equals', 'not_equals']),
        validator: minLengthValidator.bind(null, 5)
      },
      {
        name: 'idParentInstitution',
        label: 'Parent Institution ID',
        datatype: 'textarea',
        placeholder: 'Comma separated list of Parent Institution IDs (max 10000 characters)',
        operators: getOperators(['equals', 'not_equals']),
        validator: minLengthValidator.bind(null, 5)
      },
      {
        name: 'nrImportantUrls',
        label: 'Nr. of Important URLs',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['equals', 'gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'nrEmailDomains',
        label: 'Nr. of Email Domains',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['equals', 'gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'nrInstitutions',
        label: 'Nr. of Institutions',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['equals', 'gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'nrLinkedDomains',
        label: 'Nr. of Linked Domains',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['equals', 'gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'vendor_name',
        label: 'Vendor Name',
        defaultOperator: 'equals',
        operators: getOperators(['equals', 'contains', 'does_not_contain', 'contains_word', 'starts_with', 'ends_with', 'exists']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'description',
        label: 'Description',
        defaultOperator: 'equals',
        operators: getOperators(['equals', 'contains', 'does_not_contain', 'contains_word', 'starts_with', 'ends_with', 'exists']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'contract_duration',
        label: 'Contract Length',
        datatype: 'number',
        defaultOperator: 'equals',
        operators: getOperators(['equals', 'gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'created_at',
        label: 'Created Date',
        defaultOperator: 'gt',
        operators: getOperators(['equals', 'gt', 'lt', 'between']),
        validator: minLengthValidator.bind(null, 1)
      },
      {
        name: 'fsclRevTotal',
        label: 'Annual Revenue',
        datatype: 'number',
        defaultOperator: 'gt',
        operators: getOperators(['gt', 'lt', 'between']),
        validator: numericValidator.bind(null)
      },
      {
        name: 'sourceAndAge',
        label: 'Source and Age',
        datatype: 'creatable',
        defaultValue: '',
        values: [
          { name: 'DISTRICT_0_3', label: 'Verified by the district within 3 months' },
          { name: 'DISTRICT_0_6', label: 'Verified by the district within 6 months' },
          { name: 'DISTRICT_0_12', label: 'Verified by the district within 12 months' },
          { name: 'STAFF_URL_0_3', label: 'Found on staff directory within 3 months' },
          { name: 'STAFF_URL_0_6', label: 'Found on staff directory within 6 months' },
          { name: 'STAFF_URL_0_12', label: 'Found on staff directory within 12 months' },
          { name: 'OTHER_URL_0_3', label: 'Found outside the staff directory within 3 months' },
          { name: 'OTHER_URL_0_6', label: 'Found outside the staff directory within 6 months' },
          { name: 'OTHER_URL_0_12', label: 'Found outside the staff directory within 12 months' },
        ],
        operators: getOperators(['in', 'not_in']),
        customOptions: {
          getValue: (it: string) => {
            const labels: Record<string, string> = {
              'DISTRICT_0_3': 'District ≤3 mo',
              'DISTRICT_0_6': 'District ≤6 mo',
              'DISTRICT_0_12': 'District ≤12 mo',
              'STAFF_URL_0_3': 'Staff directory ≤3 mo',
              'STAFF_URL_0_6': 'Staff directory ≤6 mo',
              'STAFF_URL_0_12': 'Staff directory ≤12 mo',
              'OTHER_URL_0_3': 'Other page ≤3 mo',
              'OTHER_URL_0_6': 'Other page ≤6 mo',
              'OTHER_URL_0_12': 'Other page ≤12 mo',
            };
            return labels[it] || it;
          }
        }
      },
      {
        name: 'lastSeen',
        label: 'Last Seen',
        valueEditorType: 'select',
        defaultOperator: 'equals',
        defaultValue: 'LAST_SEEN_0_3',
        values: [
          { name: 'LAST_SEEN_0_3', label: 'Within 3 months' },
          { name: 'LAST_SEEN_0_6', label: 'Within 6 months' },
          { name: 'LAST_SEEN_0_12', label: 'Within 12 months' },
        ],
        operators: getOperators(['equals', 'not_equals'])
      }
    ];

    return fields
      .filter(field => supportedFields.includes(field.name))
      .map(field => type === 'contacts' && contactFieldLabels[field.name]
        ? { ...field, label: contactFieldLabels[field.name] }
        : field);
  }

  function getControlElements(): Partial<Controls> {
    const controlElements: Partial<Controls> = {
      addGroupAction: props => (props.level === 0 ? null : null),
      addRuleAction: props => <ActionElement {...props} label='Add Filter' />,
      removeRuleAction: props => <ActionElement {...props} label='✗' />,
      removeGroupAction: props => <ActionElement {...props} label='✗' />,
      cloneRuleAction: props => <ActionElement {...props} />,
      valueEditor: CustomValueEditor,
    }

    if (type === 'contacts') {
      controlElements.fieldSelector = CustomSourceSelector
    } else if (type === 'institutions') {
      controlElements.fieldSelector = InstitutionFieldSelector
    }

    return controlElements;
  }

  return (
    <div>
      <QueryBuilder
        fields={getFields()}
        showCloneButtons={true}
        query={query}
        onQueryChange={onQueryChange}
        combinators={combinators}
        controlClassnames={{
          queryBuilder: 'queryBuilder-branches',
        }}
        controlElements={getControlElements()}
        context={{ authInfo }}
      />
    </div>
  )
}
